import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { deckKeys } from '@/entities/deck';
import { ReviewSession } from '@/features/review';
import { getDeck } from '@/shared/api/decks';
import { ErrorMessage } from '@/shared/ui';

import styles from './ReviewPage.module.css';

/**
 * The study screen, at /review/all or /review/<deck>. It always says whether it
 * covers one deck or all of them, and leads back to the choice.
 */
export function ReviewPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { scope = 'all' } = useParams();
  const deckId = scope === 'all' ? undefined : scope;

  const deck = useQuery({
    queryKey: deckKeys.one(deckId ?? ''),
    queryFn: ({ signal }) => getDeck(deckId ?? '', signal),
    enabled: deckId !== undefined,
  });

  if (deck.isError) {
    return (
      <div className={styles.page}>
        <ErrorMessage error={deck.error} />
        <Link to="/review">{t('review.choose')}</Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.scope}>
        <h1>
          {deckId ? t('review.deck', { title: deck.data?.title ?? '…' }) : t('review.allDecks')}
        </h1>
        <Link to="/review">{t('review.choose')}</Link>
      </header>
      <ReviewSession key={scope} {...(deckId ? { deckId } : {})} />
    </div>
  );
}
